/**
 * Conversation History Service
 * Manages conversation storage, retrieval, and operations using localStorage
 */

/**
 * Generate storage keys based on chatId
 * @param {string} chatId - Chat identifier (e.g., 'entrepreneur_tool', 'teacher_chat')
 * @returns {Object} Object with storage and current conversation keys
 */
const getStorageKeys = (chatId = 'ai_text_generator') => {
  return {
    storageKey: `chat_history_${chatId}`,
    currentKey: `chat_current_${chatId}`
  };
};

/**
 * Generate automatic title from first user message
 * @param {string} firstMessage - First user message in conversation
 * @returns {string} Generated title (max 50 chars)
 */
export const generateConversationTitle = (firstMessage) => {
  if (!firstMessage) return 'Nova Conversa';
  
  // Truncate and clean the message for title
  const cleanMessage = firstMessage?.trim()?.replace(/\n/g, ' ');
  const maxLength = 50;
  
  if (cleanMessage?.length <= maxLength) {
    return cleanMessage;
  }
  
  return cleanMessage?.substring(0, maxLength - 3) + '...';
};

/**
 * Get all saved conversations
 * @param {string} chatId - Chat identifier for isolated storage
 * @returns {Array} Array of conversation objects
 */
export const getAllConversations = (chatId = 'ai_text_generator') => {
  try {
    const { storageKey } = getStorageKeys(chatId);
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading conversations:', error);
    return [];
  }
};

/**
 * Save a new conversation or update existing one
 * @param {Object} conversation - Conversation object with id, title, messages, timestamp
 * @param {string} chatId - Chat identifier for isolated storage
 * @returns {Object} Saved conversation
 */
export const saveConversation = (conversation, chatId = 'ai_text_generator') => {
  try {
    const conversations = getAllConversations(chatId);
    const existingIndex = conversations?.findIndex(c => c?.id === conversation?.id);
    
    if (existingIndex >= 0) {
      // Update existing conversation
      conversations[existingIndex] = {
        ...conversations?.[existingIndex],
        ...conversation,
        updatedAt: new Date()?.toISOString()
      };
    } else {
      // Add new conversation
      const newConversation = {
        id: conversation?.id || Date.now()?.toString(),
        title: conversation?.title || generateConversationTitle(conversation?.messages?.[0]?.content),
        messages: conversation?.messages || [],
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      };
      conversations?.unshift(newConversation);
    }
    
    const { storageKey } = getStorageKeys(chatId);
    localStorage.setItem(storageKey, JSON.stringify(conversations));
    return conversations?.find(c => c?.id === conversation?.id);
  } catch (error) {
    console.error('Error saving conversation:', error);
    throw error;
  }
};

/**
 * Get a specific conversation by ID
 * @param {string} conversationId - Conversation ID
 * @param {string} chatId - Chat identifier for isolated storage
 * @returns {Object|null} Conversation object or null
 */
export const getConversation = (conversationId, chatId = 'ai_text_generator') => {
  try {
    const conversations = getAllConversations(chatId);
    return conversations?.find(c => c?.id === conversationId) || null;
  } catch (error) {
    console.error('Error loading conversation:', error);
    return null;
  }
};

/**
 * Delete a conversation
 * @param {string} conversationId - Conversation ID to delete
 * @param {string} chatId - Chat identifier for isolated storage
 * @returns {boolean} Success status
 */
export const deleteConversation = (conversationId, chatId = 'ai_text_generator') => {
  try {
    const conversations = getAllConversations(chatId);
    const filtered = conversations?.filter(c => c?.id !== conversationId);
    const { storageKey } = getStorageKeys(chatId);
    localStorage.setItem(storageKey, JSON.stringify(filtered));
    
    // Clear current conversation if it's the deleted one
    const currentId = getCurrentConversationId(chatId);
    if (currentId === conversationId) {
      clearCurrentConversation(chatId);
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting conversation:', error);
    return false;
  }
};

/**
 * Rename a conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} newTitle - New title for the conversation
 * @param {string} chatId - Chat identifier for isolated storage
 * @returns {boolean} Success status
 */
export const renameConversation = (conversationId, newTitle, chatId = 'ai_text_generator') => {
  try {
    const conversations = getAllConversations(chatId);
    const conversation = conversations?.find(c => c?.id === conversationId);
    
    if (conversation) {
      conversation.title = newTitle?.trim() || 'Nova Conversa';
      conversation.updatedAt = new Date()?.toISOString();
      const { storageKey } = getStorageKeys(chatId);
      localStorage.setItem(storageKey, JSON.stringify(conversations));
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error renaming conversation:', error);
    return false;
  }
};

/**
 * Set current active conversation ID
 * @param {string} conversationId - Conversation ID
 * @param {string} chatId - Chat identifier for isolated storage
 */
export const setCurrentConversationId = (conversationId, chatId = 'ai_text_generator') => {
  try {
    const { currentKey } = getStorageKeys(chatId);
    localStorage.setItem(currentKey, conversationId);
  } catch (error) {
    console.error('Error setting current conversation:', error);
  }
};

/**
 * Get current active conversation ID
 * @param {string} chatId - Chat identifier for isolated storage
 * @returns {string|null} Current conversation ID or null
 */
export const getCurrentConversationId = (chatId = 'ai_text_generator') => {
  try {
    const { currentKey } = getStorageKeys(chatId);
    return localStorage.getItem(currentKey);
  } catch (error) {
    console.error('Error getting current conversation:', error);
    return null;
  }
};

/**
 * Clear current conversation ID
 * @param {string} chatId - Chat identifier for isolated storage
 */
export const clearCurrentConversation = (chatId = 'ai_text_generator') => {
  try {
    const { currentKey } = getStorageKeys(chatId);
    localStorage.removeItem(currentKey);
  } catch (error) {
    console.error('Error clearing current conversation:', error);
  }
};

/**
 * Auto-save current conversation
 * @param {string} conversationId - Conversation ID
 * @param {Array} messages - Array of messages
 * @param {string} chatId - Chat identifier for isolated storage
 * @returns {Object} Saved conversation
 */
export const autoSaveConversation = (conversationId, messages, chatId = 'ai_text_generator') => {
  if (!messages || messages?.length === 0) return null;
  
  const firstUserMessage = messages?.find(m => m?.role === 'user');
  const title = generateConversationTitle(firstUserMessage?.content);
  
  return saveConversation({
    id: conversationId,
    title,
    messages
  }, chatId);
};

/**
 * Clear all conversations (for testing or reset)
 * @param {string} chatId - Chat identifier for isolated storage
 * @returns {boolean} Success status
 */
export const clearAllConversations = (chatId = 'ai_text_generator') => {
  try {
    const { storageKey, currentKey } = getStorageKeys(chatId);
    localStorage.removeItem(storageKey);
    localStorage.removeItem(currentKey);
    return true;
  } catch (error) {
    console.error('Error clearing all conversations:', error);
    return false;
  }
};